import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Form,
  Alert,
  Spinner,
  Modal,
  InputGroup,
} from "react-bootstrap";
import { Icon } from "../../utils/iconHelper";
import RubricService, { Rubric } from "../../_services/RubricService";
import RubricFormModal from "./components/RubricFormModal";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import "./RubricsPage.css";

const RubricsPage: React.FC = () => {
  useDocumentTitle("Rubrics");

  // State management
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingRubric, setEditingRubric] = useState<Rubric | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingRubric, setDeletingRubric] = useState<Rubric | null>(null);

  // Expanded rubric for preview
  const [expandedRubricId, setExpandedRubricId] = useState<number | null>(null);

  useEffect(() => {
    fetchRubrics();
  }, [currentPage, includeInactive]);

  const fetchRubrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await RubricService.getAllRubrics({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        includeInactive,
      });
      if (response.success) {
        setRubrics(response.data.rubrics);
        setTotalCount(response.data.pagination.total);
      } else {
        setError("Failed to fetch rubrics");
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch rubrics");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchRubrics();
  };

  const handleCreate = () => {
    setEditingRubric(null);
    setShowFormModal(true);
  };

  const handleEdit = (rubric: Rubric) => {
    setEditingRubric(rubric);
    setShowFormModal(true);
  };

  const handleDelete = (rubric: Rubric) => {
    setDeletingRubric(rubric);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingRubric?.id) return;

    try {
      const response = await RubricService.deleteRubric(deletingRubric.id);
      if (response.success) {
        setSuccess(`Rubric "${deletingRubric.name}" deleted successfully`);
        setShowDeleteModal(false);
        setDeletingRubric(null);
        fetchRubrics();
      } else {
        setError(response.message || "Failed to delete rubric");
      }
    } catch (err: any) {
      setError(err.message || "Failed to delete rubric");
    }
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    setEditingRubric(null);
    setSuccess(editingRubric ? "Rubric updated successfully" : "Rubric created successfully");
    fetchRubrics();
  };

  const toggleExpand = (rubricId: number) => {
    setExpandedRubricId(expandedRubricId === rubricId ? null : rubricId);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="rubrics-page">
      {/* Header */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-1">Grading Rubrics</h2>
            <p className="text-muted mb-0">Manage rubrics for evaluating student submissions</p>
          </div>
          <Button
            variant="primary"
            onClick={handleCreate}
            className="d-flex align-items-center"
          >
            <Icon.Plus className="me-2" />
            Create New Rubric
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <div className="row g-3 align-items-center">
            <div className="col-md-6">
              <InputGroup>
                <InputGroup.Text className="bg-white border-end-0">
                  <Icon.Search size={16} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  className="border-start-0 ps-0"
                  placeholder="Search rubrics by name or code…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
                {searchTerm && (
                  <Button
                    variant="link"
                    className="border border-start-0 text-muted px-2"
                    onClick={() => {
                      setSearchTerm("");
                      setCurrentPage(1);
                      fetchRubrics();
                    }}
                    title="Clear search"
                  >
                    <Icon.X size={14} />
                  </Button>
                )}
              </InputGroup>
            </div>
            <div className="col-md-3">
              <Form.Check
                type="switch"
                id="include-inactive"
                label="Include inactive rubrics"
                checked={includeInactive}
                onChange={(e) => {
                  setIncludeInactive(e.target.checked);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="col-md-3 text-end">
              <span className="text-muted">
                <strong>{totalCount}</strong> rubrics total
              </span>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Rubrics Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner role="status" animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading rubrics...</p>
            </div>
          ) : rubrics.length === 0 ? (
            <div className="text-center py-5">
              <Icon.FileText size={48} className="text-muted mb-3" />
              <h5 className="text-muted">No rubrics found</h5>
              <p className="text-muted">Create your first rubric to get started with grading</p>
              <Button variant="primary" onClick={handleCreate} className="mt-3">
                <Icon.Plus className="me-2" />
                Create Rubric
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="rubrics-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th style={{ width: "60px" }}>ID</th>
                    <th>Name</th>
                    <th style={{ width: "100px" }}>Code</th>
                    <th style={{ width: "80px" }}>Max Score</th>
                    <th style={{ width: "60px" }}>Levels</th>
                    <th style={{ width: "90px" }}>Status</th>
                    <th style={{ width: "110px" }}>Created</th>
                    <th style={{ width: "120px" }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rubrics.map((rubric) => (
                    <React.Fragment key={rubric.id}>
                      <tr className={expandedRubricId === rubric.id ? "expanded-row" : ""}>
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => toggleExpand(rubric.id)}
                          >
                            {expandedRubricId === rubric.id ? (
                              <Icon.ChevronDown />
                            ) : (
                              <Icon.ChevronRight />
                            )}
                          </Button>
                        </td>
                        <td>
                          <span className="text-muted" style={{ fontSize: "12px" }}>{rubric.id}</span>
                        </td>
                        <td className="fw-semibold">{rubric.name}</td>
                        <td>
                          <code className="rubric-code">{rubric.code}</code>
                        </td>
                        <td>
                          <span className="score-text">{rubric.maxScore} pts</span>
                        </td>
                        <td>
                          {rubric.criterion?.levels?.length || 0}
                        </td>
                        <td>
                          {rubric.activeFlag ? (
                            <span className="status-active">Active</span>
                          ) : (
                            <span className="status-inactive">Inactive</span>
                          )}
                        </td>
                        <td>
                          {formatDate(rubric.createdDate)}
                        </td>
                        <td>
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEdit(rubric)}
                              title="Edit rubric"
                            >
                              <Icon.Edit2 size={14} />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDelete(rubric)}
                              title="Delete rubric"
                            >
                              <Icon.Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expandedRubricId === rubric.id && (
                        <tr className="rubric-preview-row">
                          <td colSpan={9}>
                            <div className="rubric-preview p-3">
                              <h6 className="mb-3">Rubric Levels</h6>
                              <div className="rubric-levels-grid">
                                {rubric.criterion?.levels?.map((level, idx) => (
                                  <div key={idx} className="rubric-level-card">
                                    <div className="level-header">
                                      <span className="level-name">{level.level}</span>
                                      <span className="level-score">{level.score} pts</span>
                                    </div>
                                    <p className="level-description">{level.description}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Form Modal */}
      <RubricFormModal
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
        rubric={editingRubric}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this rubric?</p>
          {deletingRubric && (
            <Alert variant="warning" className="mb-0">
              <strong>{deletingRubric.name}</strong>
              <br />
              <small className="text-muted">Code: {deletingRubric.code}</small>
            </Alert>
          )}
          <p className="mt-3 text-muted small">
            Note: This will mark the rubric as inactive. Study resources using this rubric may be affected.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RubricsPage;
